import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CargoService } from 'src/app/service/cargo.service';
import { ColaboradorService } from 'src/app/service/colaborador.service';
import { DepartamentoService } from 'src/app/service/departamento.service';
import { formatarDataApi, parseDataApi } from 'src/app/util/date-format.util';
import { Cargo } from '../cargo/cargo';
import { Departamento } from '../departamento/departamento';
import { Colaborador, ColaboradorDocumento } from './colaborador';

@Component({
  selector: 'app-colaborador-form',
  templateUrl: './colaborador-form.component.html',
  styleUrls: ['./colaborador-shared.scss'],
})
export class ColaboradorFormComponent implements OnInit {

  formulario: FormGroup;
  carregando = false;
  salvando = false;
  departamentos: Departamento[] = [];
  cargos: Cargo[] = [];
  documentos: ColaboradorDocumento[] = [];
  enviandoDocumento = false;

  constructor(
    private fb: FormBuilder,
    private service: ColaboradorService,
    private departamentoService: DepartamentoService,
    private cargoService: CargoService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.montarForm();
    this.carregarDepartamentos();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.carregar(+id);
    }
  }

  get colaboradorId(): number | null {
    return this.formulario?.value?.id ?? null;
  }

  montarForm(item?: Colaborador) {
    this.formulario = this.fb.group({
      id: [item?.id ?? null],
      nome: [item?.nome ?? '', Validators.required],
      cpf: [item?.cpf ?? ''],
      rg: [item?.rg ?? ''],
      email: [item?.email ?? ''],
      telefone: [item?.telefone ?? ''],
      dataNascimento: [parseDataApi(item?.dataNascimento)],
      dataAdmissao: [parseDataApi(item?.dataAdmissao)],
      dataDemissao: [parseDataApi(item?.dataDemissao)],
      departamentoId: [item?.departamentoId ?? null, Validators.required],
      cargoId: [item?.cargoId ?? null, Validators.required],
      ativo: [item?.ativo ?? true],
      observacao: [item?.observacao ?? ''],
    });
    if (item?.departamentoId) {
      this.carregarCargos(item.departamentoId, item.cargoId ?? null);
    }
    this.formulario.get('departamentoId')?.valueChanges.subscribe(deptoId => {
      this.formulario.patchValue({ cargoId: null }, { emitEvent: false });
      this.carregarCargos(deptoId);
    });
    this.documentos = item?.documentos ?? [];
  }

  carregarDepartamentos() {
    this.departamentoService.listarAtivos().subscribe(lista => this.departamentos = lista ?? []);
  }

  carregarCargos(departamentoId: number | null, cargoIdSelecionado: number | null = null) {
    if (!departamentoId) {
      this.cargos = [];
      return;
    }
    this.cargoService.listarAtivos(departamentoId).subscribe(lista => {
      this.cargos = lista ?? [];
      if (cargoIdSelecionado) {
        this.formulario.patchValue({ cargoId: cargoIdSelecionado }, { emitEvent: false });
      }
    });
  }

  carregar(id: number) {
    this.carregando = true;
    this.service.buscarPorId(id).subscribe(item => {
      this.montarForm(item);
      this.carregando = false;
    }, () => {
      this.carregando = false;
      this.snackBar.open('Colaborador não encontrado.', 'ERRO', { duration: 2500 });
      this.voltarConsulta();
    });
  }

  submit() {
    if (this.formulario.invalid) {
      this.formulario.markAllAsTouched();
      this.snackBar.open('Preencha os campos obrigatórios (nome, departamento e cargo).', 'ERRO', { duration: 3000 });
      return;
    }
    const raw = this.formulario.value;
    const payload: Colaborador = {
      ...raw,
      dataNascimento: formatarDataApi(raw.dataNascimento, ''),
      dataAdmissao: formatarDataApi(raw.dataAdmissao, ''),
      dataDemissao: formatarDataApi(raw.dataDemissao, ''),
    };
    this.salvando = true;
    const ehNovo = !payload.id;
    this.service.salvar(payload).subscribe(salvo => {
      this.salvando = false;
      this.snackBar.open('Colaborador salvo com sucesso.', 'OK', { duration: 2500 });
      this.formulario.patchValue({ id: salvo.id }, { emitEvent: false });
      this.documentos = salvo.documentos ?? this.documentos;
      if (ehNovo && salvo.id) {
        this.router.navigate(['/colaborador/form', salvo.id], { replaceUrl: true });
      } else if (salvo.id) {
        this.carregarDocumentos(salvo.id);
      }
    }, err => {
      this.salvando = false;
      this.snackBar.open(err.error?.message ?? 'Erro ao salvar.', 'ERRO', { duration: 3000 });
    });
  }

  // ── Documentos ──────────────────────────────────────────────────────────────
  carregarDocumentos(id: number) {
    this.service.listarDocumentos(id).subscribe(lista => this.documentos = lista ?? []);
  }

  selecionarArquivo(input: HTMLInputElement) {
    if (!this.colaboradorId) {
      this.snackBar.open('Salve o colaborador antes de anexar documentos.', 'ATENÇÃO', { duration: 3000 });
      return;
    }
    input.click();
  }

  onArquivoSelecionado(event: Event) {
    const input = event.target as HTMLInputElement;
    const arquivo = input.files && input.files.length ? input.files[0] : null;
    if (!arquivo || !this.colaboradorId) {
      return;
    }
    this.enviandoDocumento = true;
    this.service.anexarDocumento(this.colaboradorId, arquivo).subscribe(() => {
      this.enviandoDocumento = false;
      input.value = '';
      this.snackBar.open('Documento anexado.', 'OK', { duration: 2000 });
      this.carregarDocumentos(this.colaboradorId!);
    }, err => {
      this.enviandoDocumento = false;
      input.value = '';
      this.snackBar.open(err.error?.message ?? 'Erro ao anexar documento.', 'ERRO', { duration: 3000 });
    });
  }

  baixarDocumento(doc: ColaboradorDocumento) {
    if (!this.colaboradorId || !doc.id) {
      return;
    }
    this.service.baixarDocumento(this.colaboradorId, doc.id).subscribe(blob => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }, () => this.snackBar.open('Erro ao abrir documento.', 'ERRO', { duration: 2500 }));
  }

  removerDocumento(doc: ColaboradorDocumento) {
    if (!this.colaboradorId || !doc.id || !confirm('Remover este documento?')) {
      return;
    }
    this.service.removerDocumento(this.colaboradorId, doc.id).subscribe(() => {
      this.snackBar.open('Documento removido.', 'OK', { duration: 2000 });
      this.carregarDocumentos(this.colaboradorId!);
    }, () => this.snackBar.open('Erro ao remover documento.', 'ERRO', { duration: 2500 }));
  }

  formatarTamanho(bytes?: number): string {
    if (!bytes && bytes !== 0) {
      return '';
    }
    if (bytes < 1024) {
      return bytes + ' B';
    }
    if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    }
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  ehImagem(doc: ColaboradorDocumento): boolean {
    return !!doc.mimeType && doc.mimeType.startsWith('image/');
  }

  limpar() {
    this.router.navigate(['/colaborador/form']);
    this.montarForm();
  }

  voltarConsulta() {
    this.router.navigate(['/colaborador/consulta']);
  }
}
